import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { BookingInfo } from "@/types/booking";
import { supabase } from "@/integrations/supabase/client";

export type PaymentMode = 'setup' | 'payment';

export const useNewPaymentFlow = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Initialize payment - redirect to Stripe Checkout
  const initializePayment = useCallback(async (
    bookingId: string,
    bookingInfo: BookingInfo,
    selectedDate: Date,
    couponCode: string | null = null,
    discountPercent: number = 0
  ): Promise<boolean> => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please log in to complete your booking."
      });
      navigate('/client-auth');
      return false;
    }

    // Check session is valid before proceeding
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        variant: "destructive",
        title: "Session Expired",
        description: "Please log in again to continue booking."
      });
      navigate('/client-auth');
      return false;
    }

    setIsProcessing(true);

    try {
      // Handle free bookings - set to Reserved/Free for practitioner confirmation
      if (bookingInfo.price === 0) {
        const confirmationDeadline = new Date();
        confirmationDeadline.setHours(confirmationDeadline.getHours() + 48);

        const { error } = await supabase
          .from('bookings')
          .update({
            status: 'Reserved',
            payment_status: 'Free',
            confirmation_deadline: confirmationDeadline.toISOString(),
            reserved_until: null,
          })
          .eq('id', bookingId);

        if (error) throw error;

        toast({
          title: "Booking Submitted!",
          description: "Your free session is pending practitioner confirmation."
        });

        navigate('/client-dashboard');
        setIsProcessing(false);
        return false;
      }

      // Calculate days until session
      const daysUntilSession = Math.ceil((selectedDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      console.log(`Days until session: ${daysUntilSession}`);

      // Determine mode: > 3 days = setup (save card), <= 3 days = payment (authorize)
      const mode = daysUntilSession > 3 ? 'setup' : 'payment';
      console.log(`Creating Stripe Checkout session in ${mode} mode...`);

      // Call the new checkout session endpoint
      const { data, error } = await supabase.functions.invoke('create-checkout-session-v2', {
        body: { 
          bookingId, 
          couponCode, 
          discountPercent,
          mode
        }
      });

      if (error) {
        console.error('Checkout session error:', error);
        throw new Error(error.message || 'Failed to create checkout session');
      }

      if (!data?.url) {
        throw new Error('No checkout URL returned');
      }

      console.log('Redirecting to Stripe Checkout:', data.url);
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
      
      // Return false since we're redirecting, not showing inline form
      return false;

    } catch (error: any) {
      console.error('Payment initialization error:', error);
      toast({
        variant: "destructive",
        title: "Payment Setup Failed",
        description: error.message || "There was an error setting up payment"
      });
      setIsProcessing(false);
      return false;
    }
  }, [user, toast, navigate]);

  // Handle payment errors
  const handlePaymentError = useCallback(async (error: string, bookingId?: string) => {
    toast({
      variant: "destructive",
      title: "Payment Error",
      description: error
    });

    if (bookingId) {
      try {
        console.log('Payment failed, updating booking:', bookingId);
        
        await supabase
          .from('bookings')
          .update({
            status: 'Cancelled - Payment Failed',
            payment_status: 'Failed',
            updated_at: new Date().toISOString()
          })
          .eq('id', bookingId);

        await supabase.functions.invoke('send-email', {
          body: {
            template_type: 'paymentHoldFailed',
            booking_id: bookingId,
            failure_reason: error
          }
        });
      } catch (cancelError) {
        console.error('Failed to cancel booking after payment failure:', cancelError);
      }
    }
  }, [toast]);

  const resetPaymentState = useCallback(() => {
    setIsProcessing(false);
  }, []);

  return {
    isProcessing,
    initializePayment,
    handlePaymentError,
    resetPaymentState
  };
};
